import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import AddressAutocomplete from "../shared/AddressAutocomplete/AddressAutocomplete";
import {
  Modal,
  TextInput,
  Textarea,
  Button,
  Group,
  Stack,
  MultiSelect,
  Loader,
} from "@mantine/core";
import { IconCalendar, IconUsers } from "@tabler/icons-react";
import "./CreateTeamEventModal.css";
import { handleGenericError } from "../../utils/errorHandler";

const MEETING_TYPE_OPTIONS = [
  { value: "team_meeting", label: "Team Meeting" },
  { value: "rep_meeting", label: "Rep Meeting" },
  { value: "recruiting_meeting", label: "Recruiting Meeting" },
];

function CreateTeamEventModal({
  isOpen,
  onClose,
  onEventCreated,
  initialDateTime = null,
  initialMeetingType = "team_meeting",
  event = null,
}) {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess, showError } = useToast();
  const isEditMode = Boolean(event?.id);

  const [formData, setFormData] = useState({
    meeting_type: "team_meeting",
    event_title: "",
    event_description: "",
    scheduled_time: new Date(),
    duration_minutes: 60,
    location: "",
    assigned_rep_ids: [],
    assigned_group_ids: [],
  });

  // Calendar and time selector state
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);

  const [reps, setReps] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingReps, setLoadingReps] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    fetchReps();
    fetchGroups();

    if (event) {
      const eventStart =
        event.start instanceof Date ? event.start : new Date(event.start);
      const eventDuration = Math.max(
        15,
        event.duration_minutes ||
          Math.round(
            ((event.end instanceof Date ? event.end : new Date(event.end)) -
              eventStart) /
              (1000 * 60)
          ) ||
          60
      );

      setFormData({
        meeting_type: initialMeetingType || "team_meeting",
        event_title: event.title || "",
        event_description: event.description || "",
        scheduled_time: eventStart,
        duration_minutes: eventDuration,
        location: event.location || "",
        assigned_rep_ids: [],
        assigned_group_ids: [],
      });
      setCurrentMonth(
        new Date(eventStart.getFullYear(), eventStart.getMonth(), 1)
      );
      setSelectedDate(eventStart);
      const slots = generateTimeSlots(eventStart);
      setAvailableSlots(slots);
      const matchedSlot = slots.find(
        slot => slot.dateTime.getTime() === eventStart.getTime()
      );
      setSelectedTime(matchedSlot || null);
      setShowCalendar(false);
      return;
    }

    const prefillDate =
      initialDateTime instanceof Date
        ? new Date(initialDateTime)
        : initialDateTime
          ? new Date(initialDateTime)
          : null;

    if (prefillDate && !Number.isNaN(prefillDate.getTime())) {
      setFormData(prev => ({ ...prev, scheduled_time: prefillDate }));
      setFormData(prev => ({
        ...prev,
        meeting_type: initialMeetingType || "team_meeting",
        scheduled_time: prefillDate,
      }));
      setCurrentMonth(
        new Date(prefillDate.getFullYear(), prefillDate.getMonth(), 1)
      );
      setSelectedDate(prefillDate);
      const slots = generateTimeSlots(prefillDate);
      setAvailableSlots(slots);

      const matchedSlot = slots.find(
        slot => slot.dateTime.getTime() === prefillDate.getTime()
      );
      setSelectedTime(matchedSlot || null);
    } else {
      const now = new Date();
      setFormData(prev => ({
        ...prev,
        meeting_type: initialMeetingType || "team_meeting",
        scheduled_time: now,
      }));
      setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
      setSelectedDate(null);
      setSelectedTime(null);
      setAvailableSlots([]);
    }
    setShowCalendar(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialDateTime, event, initialMeetingType]);

  const handleMeetingTypeChange = nextType => {
    const selectedType = MEETING_TYPE_OPTIONS.find(
      option => option.value === nextType
    );
    const currentType = MEETING_TYPE_OPTIONS.find(
      option => option.value === formData.meeting_type
    );

    setFormData(prev => ({
      ...prev,
      meeting_type: nextType,
      event_title:
        !prev.event_title.trim() ||
        (currentType && prev.event_title.trim() === currentType.label)
          ? selectedType?.label || ""
          : prev.event_title,
    }));
  };

  const fetchReps = async () => {
    try {
      setLoadingReps(true);
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getApiEndpoint("/v1/company-downline/reps-by-manager"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        const repsData = result.data?.reps || [];

        setReps(
          repsData.map(rep => ({
            value: rep.rep_id.toString(),
            label: `${rep.first_name} ${rep.last_name}`,
          }))
        );
      }
    } catch (error) {
      // Error fetching reps
    } finally {
      setLoadingReps(false);
    }
  };

  const fetchGroups = async () => {
    try {
      setLoadingGroups(true);
      const jwt = await getVerifiedJWT();
      const response = await fetch(getApiEndpoint("/v1/groups/owned"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const groupsData = result.data || [];

        setGroups(
          groupsData.map(group => ({
            value: group.id.toString(),
            label: group.name,
          }))
        );
      }
    } catch (error) {
      // Error fetching groups
    } finally {
      setLoadingGroups(false);
    }
  };

  // Generate available time slots for a given date
  const generateTimeSlots = date => {
    const slots = [];
    const startHour = 9; // 9 AM
    const endHour = 17; // 5 PM
    const interval = formData.duration_minutes; // Use duration as interval

    // For durations over 60 minutes, still use hourly intervals but mark the duration
    let minuteInterval = interval;
    if (interval > 60) {
      minuteInterval = 60; // Use hourly intervals for 90+ min durations
    }

    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += minuteInterval) {
        const timeDate = new Date(date);
        timeDate.setHours(hour, minute, 0, 0);

        const slotEnd = new Date(timeDate);
        slotEnd.setMinutes(slotEnd.getMinutes() + formData.duration_minutes);

        // Check if this slot + duration doesn't exceed end hour
        const endHourComponents = slotEnd.getHours();
        if (endHourComponents > endHour) {
          continue;
        }

        const timeString = timeDate.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
        slots.push({
          time: timeString,
          dateTime: timeDate,
        });
      }
    }

    return slots;
  };

  // Get calendar data for current month
  const getDaysInMonth = date => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  // Handle date selection
  const handleDateSelect = day => {
    const selected = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    setSelectedDate(selected);
    setSelectedTime(null);

    // Generate available time slots for selected date
    const slots = generateTimeSlots(selected);
    setAvailableSlots(slots);
  };

  // Handle time slot selection
  const handleTimeSelect = slot => {
    setSelectedTime(slot);
    // Update formData with the selected date/time
    setFormData({ ...formData, scheduled_time: slot.dateTime });
    setShowCalendar(false);
  };

  // Navigate to previous month
  const previousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
    );
    setSelectedDate(null);
    setSelectedTime(null);
  };

  // Navigate to next month
  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
    );
    setSelectedDate(null);
    setSelectedTime(null);
  };

  // Check if date is available (future dates within 60 days)
  const isDateAvailable = day => {
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 60);

    return date >= today && date <= maxDate;
  };

  // Check if date is today
  const isToday = day => {
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Check if date is selected
  const isSelected = day => {
    if (!selectedDate) return false;
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === currentMonth.getMonth() &&
      selectedDate.getFullYear() === currentMonth.getFullYear()
    );
  };

  // Regenerate time slots when duration changes
  useEffect(() => {
    if (selectedDate) {
      const slots = generateTimeSlots(selectedDate);
      setAvailableSlots(slots);
      setSelectedTime(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.duration_minutes]);

  const handleSubmit = async () => {
    // Validation
    if (!formData.event_title.trim()) {
      showError("Event title is required");
      return;
    }

    try {
      setLoading(true);
      const jwt = await getVerifiedJWT();

      // Ensure scheduled_time is a Date object
      const scheduledTimeDate =
        formData.scheduled_time instanceof Date
          ? formData.scheduled_time
          : new Date(formData.scheduled_time);

      const payload = {
        event_title: formData.event_title,
        event_description: formData.event_description,
        scheduled_time: scheduledTimeDate.toISOString(),
        duration_minutes: formData.duration_minutes,
        location: formData.location,
      };

      const response = await fetch(
        getApiEndpoint(
          isEditMode
            ? `/v1/attendance/team-events/${event.id}`
            : "/v1/attendance/team-events"
        ),
        {
          method: isEditMode ? "PATCH" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(
            isEditMode
              ? payload
              : {
                  ...payload,
                  assigned_rep_ids: formData.assigned_rep_ids.map(id =>
                    parseInt(id)
                  ),
                  assigned_group_ids: formData.assigned_group_ids.map(id =>
                    parseInt(id)
                  ),
                }
          ),
        }
      );

      if (!response.ok) {
        let message = isEditMode
          ? "Failed to update team event"
          : "Failed to create team event";
        try {
          const err = await response.json();
          message = err?.message || err?.error || err?.data?.message || message;
        } catch {
          // keep fallback message
        }
        throw new Error(message);
      }

      const result = await response.json().catch(() => ({}));

      if (!isEditMode) {
        showSuccess(
          `Team event created for ${result.data.created_count} rep${result.data.created_count !== 1 ? "s" : ""}`
        );
      }

      // Reset form
      setFormData({
        meeting_type: initialMeetingType || "team_meeting",
        event_title: "",
        event_description: "",
        scheduled_time: new Date(),
        duration_minutes: 60,
        location: "",
        assigned_rep_ids: [],
        assigned_group_ids: [],
      });

      if (onEventCreated) {
        onEventCreated(result);
      }
      onClose();
    } catch (error) {
      handleGenericError(
        error,
        isEditMode
          ? "Failed to update team event"
          : "Failed to create team event"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={isEditMode ? "Edit Team Event" : "Create Team Event"}
      size="xl"
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      classNames={{ root: "create-team-event-modal" }}
    >
      <Stack gap="md">
        <div
          className="create-team-event-type-toggle"
          role="tablist"
          aria-label="Meeting type"
        >
          {MEETING_TYPE_OPTIONS.map(option => (
            <button
              key={option.value}
              type="button"
              className={`create-team-event-type-option ${formData.meeting_type === option.value ? "active" : ""}`}
              onClick={() => handleMeetingTypeChange(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <TextInput
          label="Event Title"
          placeholder="Weekly Team Meeting"
          value={formData.event_title}
          onChange={e =>
            setFormData({ ...formData, event_title: e.target.value })
          }
          required
          leftSection={<IconCalendar size={16} />}
        />

        <Textarea
          label="Event Description"
          placeholder="Describe the meeting agenda..."
          value={formData.event_description}
          onChange={e =>
            setFormData({ ...formData, event_description: e.target.value })
          }
          minRows={3}
        />

        {/* Custom Calendar and Time Selector */}
        <div className="custom-datetime-section">
          <label className="custom-datetime-label">
            Date & Time <span style={{ color: "#fa5252" }}>*</span>
          </label>

          {/* Selected Date/Time Display */}
          <div
            className="custom-datetime-display"
            onClick={() => setShowCalendar(!showCalendar)}
          >
            <IconCalendar size={16} />
            <span>
              {formData.scheduled_time
                ? new Date(formData.scheduled_time).toLocaleDateString(
                    "en-US",
                    {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    }
                  ) +
                  " at " +
                  new Date(formData.scheduled_time).toLocaleTimeString(
                    "en-US",
                    {
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true,
                    }
                  )
                : "Select date and time"}
            </span>
          </div>

          {/* Calendar Popup */}
          {showCalendar && (
            <div className="custom-calendar-popup">
              {/* Duration Selector at the top */}
              <div className="custom-duration-selector">
                <label>Duration:</label>
                <select
                  value={formData.duration_minutes}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      duration_minutes: Number(e.target.value),
                    })
                  }
                >
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>60 min</option>
                  <option value={90}>90 min</option>
                  <option value={120}>120 min</option>
                </select>
              </div>

              {/* Side-by-side layout */}
              <div className="custom-calendar-time-container">
                {/* Left: Calendar */}
                <div className="custom-calendar-section">
                  <div className="custom-calendar-header">
                    <button
                      onClick={previousMonth}
                      className="calendar-nav-btn"
                    >
                      ‹
                    </button>
                    <h3>
                      {currentMonth.toLocaleDateString("en-US", {
                        month: "short",
                      })}{" "}
                      {currentMonth.getFullYear()}
                    </h3>
                    <button onClick={nextMonth} className="calendar-nav-btn">
                      ›
                    </button>
                  </div>

                  <div className="custom-calendar-grid">
                    <div className="custom-calendar-weekdays">
                      {["S", "M", "T", "W", "T", "F", "S"].map((day, idx) => (
                        <div key={idx} className="custom-calendar-weekday">
                          {day}
                        </div>
                      ))}
                    </div>

                    <div className="custom-calendar-days">
                      {(() => {
                        const { daysInMonth, startingDayOfWeek } =
                          getDaysInMonth(currentMonth);
                        const days = [];

                        // Empty days before month starts
                        for (let i = 0; i < startingDayOfWeek; i++) {
                          days.push(
                            <div
                              key={`empty-${i}`}
                              className="custom-calendar-day empty"
                            />
                          );
                        }

                        // Days of the month
                        for (let day = 1; day <= daysInMonth; day++) {
                          const available = isDateAvailable(day);
                          const today = isToday(day);
                          const selected = isSelected(day);

                          days.push(
                            <div
                              key={day}
                              className={`custom-calendar-day ${available ? "available" : "unavailable"} ${today ? "today" : ""} ${selected ? "selected" : ""}`}
                              onClick={() => available && handleDateSelect(day)}
                            >
                              {day}
                            </div>
                          );
                        }

                        return days;
                      })()}
                    </div>
                  </div>
                </div>

                {/* Right: Time Slots */}
                <div className="custom-timeslots-section">
                  <div className="custom-timeslots-header">
                    <h4>
                      {selectedDate
                        ? selectedDate.toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })
                        : "Select Date"}
                    </h4>
                  </div>

                  <div className="custom-timeslots-list">
                    {!selectedDate ? (
                      <div className="custom-timeslots-empty">
                        <p>Select a date</p>
                      </div>
                    ) : availableSlots.length === 0 ? (
                      <div className="custom-timeslots-empty">
                        <p>No times available</p>
                      </div>
                    ) : (
                      availableSlots.map((slot, index) => (
                        <button
                          key={index}
                          className={`custom-timeslot-btn ${selectedTime?.time === slot.time ? "selected" : ""}`}
                          onClick={() => handleTimeSelect(slot)}
                        >
                          {slot.time}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <AddressAutocomplete
          label="Location"
          placeholder="Conference Room A, Zoom link, etc."
          value={formData.location}
          onChange={value => setFormData({ ...formData, location: value })}
        />

        {!isEditMode && (
          <>
            <MultiSelect
              label="Assign to Groups"
              placeholder={
                loadingGroups ? "Loading groups..." : "Select groups to invite"
              }
              data={groups}
              value={formData.assigned_group_ids}
              onChange={value =>
                setFormData({ ...formData, assigned_group_ids: value })
              }
              searchable
              leftSection={<IconUsers size={16} />}
              disabled={loadingGroups}
              rightSection={loadingGroups ? <Loader size="xs" /> : null}
            />

            <MultiSelect
              label="Assign to Reps"
              placeholder={
                loadingReps ? "Loading reps..." : "Select individual reps"
              }
              data={reps}
              value={formData.assigned_rep_ids}
              onChange={value =>
                setFormData({ ...formData, assigned_rep_ids: value })
              }
              searchable
              leftSection={<IconUsers size={16} />}
              disabled={loadingReps}
              rightSection={loadingReps ? <Loader size="xs" /> : null}
            />
          </>
        )}

        <Group justify="flex-end" mt="md">
          <Button
            variant="subtle"
            color="teal"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button color="teal" onClick={handleSubmit} loading={loading}>
            {isEditMode ? "Save Changes" : "Create Event"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default CreateTeamEventModal;
